"use client";

import React, { useEffect, useState } from "react";

// A compact market clock for the header: shows the LIVE local time at a major
// stock exchange and cycles through them every few seconds (NYSE → LSE → … →
// loop). A green/grey dot marks whether that exchange is currently in session.
// Hydration-safe: renders nothing until mounted (time differs server↔client).

interface Ex { code: string; tz: string; open: number; close: number } // open/close in local decimal hours, Mon–Fri
const EXCHANGES: Ex[] = [
  { code: "NYSE", tz: "America/New_York", open: 9.5, close: 16 },
  { code: "NASDAQ", tz: "America/New_York", open: 9.5, close: 16 },
  { code: "LSE", tz: "Europe/London", open: 8, close: 16.5 },
  { code: "FWB", tz: "Europe/Berlin", open: 9, close: 17.5 },
  { code: "TSE", tz: "Asia/Tokyo", open: 9, close: 15 },
  { code: "HKEX", tz: "Asia/Hong_Kong", open: 9.5, close: 16 },
  { code: "SSE", tz: "Asia/Shanghai", open: 9.5, close: 15 },
  { code: "ASX", tz: "Australia/Sydney", open: 10, close: 16 },
];

export function MarketClock({ intervalMs = 4000 }: { intervalMs?: number }) {
  const [mounted, setMounted] = useState(false);
  const [idx, setIdx] = useState(0);
  const [, tick] = useState(0);

  useEffect(() => {
    setMounted(true);
    const cycle = setInterval(() => setIdx((x) => (x + 1) % EXCHANGES.length), intervalMs);
    const clock = setInterval(() => tick((t) => t + 1), 1000);
    return () => { clearInterval(cycle); clearInterval(clock); };
  }, [intervalMs]);

  if (!mounted) return null;

  const ex = EXCHANGES[idx];
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: ex.tz, hour: "2-digit", minute: "2-digit", hour12: false, weekday: "short",
  }).formatToParts(new Date());
  const hh = parts.find((p) => p.type === "hour")?.value ?? "00";
  const mm = parts.find((p) => p.type === "minute")?.value ?? "00";
  const wd = parts.find((p) => p.type === "weekday")?.value ?? "";
  const hourDec = parseInt(hh, 10) + parseInt(mm, 10) / 60;
  const weekday = !["Sat", "Sun"].includes(wd);
  const isOpen = weekday && hourDec >= ex.open && hourDec < ex.close;

  return (
    <span
      title={`${ex.code} · ${isOpen ? "open" : "closed"} · ${hh}:${mm} local`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
        fontFamily: "var(--font-geist-mono, ui-monospace, monospace)", fontSize: 11,
        color: "hsl(var(--muted-foreground))", padding: "0 6px",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", flex: "none", background: isOpen ? "#22c55e" : "#9ca3af" }} />
      {ex.code} {hh}:{mm}
    </span>
  );
}
